/**
 * usePlanJob — Shared hook for plan generation job lifecycle.
 *
 * Uses a global store (singleton outside React) so ALL components
 * using this hook see the same state instantly — no prop drilling,
 * no context provider needed.
 *
 * - On first mount: checks for active job via GET /active
 * - When active: opens SSE connection for real-time updates
 * - On SSE disconnect: polling fallback every 2s
 * - startJob() / abortJob() / reset() update all listeners immediately
 */

import { useSyncExternalStore, useCallback } from 'react';
import { get, post, del, getToken } from '../api';

// ── Types ──

export interface PlanJobParams {
  week_start?: string;
  meal_prep?: boolean;
  allow_repeats?: boolean;
  ingredients?: { name: string; amount?: string }[];
  extra_notes?: string;
  cuisine_preference?: string[];
  prep_type_preference?: string[];
  event_theme?: string;
  skipped_days?: string[];
}

export interface PlanSummary {
  days_planned: number;
  total_recipes: number;
  new_recipes: number;
  reused_recipes: number;
  avg_kcal_per_day: number;
  avg_protein_per_day: number;
  highlight: string;
}

interface StepEvent {
  step: number;
  label: string;
  status: 'running' | 'done';
}

interface ProgressEvent {
  current_step: number;
  total_steps: number;
  pct: number;
  detail?: string;
}

interface RecipeEvent {
  name: string;
  status: 'creating' | 'created';
}

interface DoneEvent {
  plan_id: number;
  summary: PlanSummary;
  needs_follow_up_plan?: boolean;
  follow_up_week_start?: string;
}

export interface PlanJobState {
  jobId: string | null;
  isRunning: boolean;
  status: string | null;
  currentStep: number;
  totalSteps: number;
  stepLabel: string;
  pct: number;
  steps: StepEvent[];
  recipes: RecipeEvent[];
  summary: PlanSummary | null;
  planId: number | null;
  error: string | null;
  errorStep: number | null;
  needsFollowUpPlan: boolean;
  followUpWeekStart: string | null;
}

export interface UsePlanJobReturn extends PlanJobState {
  startJob: (params: PlanJobParams) => Promise<string | null>;
  abortJob: () => Promise<void>;
  reset: () => void;
}

// ── Global Singleton Store ──

const INITIAL: PlanJobState = {
  jobId: null,
  isRunning: false,
  status: null,
  currentStep: 0,
  totalSteps: 5,
  stepLabel: '',
  pct: 0,
  steps: [],
  recipes: [],
  summary: null,
  planId: null,
  needsFollowUpPlan: false,
  followUpWeekStart: null,
  error: null,
  errorStep: null,
};

let _state: PlanJobState = { ...INITIAL };
let _listeners = new Set<() => void>();
let _sseController: AbortController | null = null;
let _pollingInterval: ReturnType<typeof setInterval> | null = null;
let _initDone = false;

function getSnapshot(): PlanJobState {
  return _state;
}

function subscribe(listener: () => void): () => void {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

function emit(partial: Partial<PlanJobState>) {
  _state = { ..._state, ...partial };
  _listeners.forEach(l => l());
}

function emitFull(state: PlanJobState) {
  _state = state;
  _listeners.forEach(l => l());
}

// ── SSE Connection ──

function connectSSE(jobId: string) {
  // Cleanup previous
  _sseController?.abort();
  stopPolling();

  const controller = new AbortController();
  _sseController = controller;
  const token = getToken();

  fetch(`/api/ai/plan-jobs/${jobId}/stream`, {
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    signal: controller.signal,
  }).then(async (response) => {
    if (!response.ok || !response.body) {
      startPolling(jobId);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.slice(6));
              handleSSEEvent(eventType, data);
            } catch { /* ignore */ }
            eventType = '';
          }
        }
      }
    } catch {
      if (controller.signal.aborted) return;
      startPolling(jobId);
    }
  }).catch(() => {
    if (controller.signal.aborted) return;
    startPolling(jobId);
  });
}

function handleSSEEvent(event: string, data: unknown) {
  const d = data as Record<string, unknown>;

  switch (event) {
    case 'state':
      emit({
        status: (d.status as string) || _state.status,
        currentStep: (d.current_step as number) || _state.currentStep,
        stepLabel: (d.step_label as string) || _state.stepLabel,
        planId: (d.plan_id as number) || _state.planId,
        summary: (d.result_summary as PlanSummary) || _state.summary,
        error: (d.error_message as string) || _state.error,
      });
      break;

    case 'step': {
      const stepEvent = d as unknown as StepEvent;
      const steps = [..._state.steps];
      const idx = steps.findIndex(s => s.step === stepEvent.step);
      if (idx >= 0) steps[idx] = stepEvent; else steps.push(stepEvent);
      emit({ steps, currentStep: stepEvent.step, stepLabel: stepEvent.label });
      break;
    }

    case 'progress': {
      const p = d as unknown as ProgressEvent;
      emit({
        currentStep: p.current_step,
        totalSteps: p.total_steps,
        pct: p.pct,
        stepLabel: p.detail ? `${_state.stepLabel.split('(')[0].trim()} (${p.detail})` : _state.stepLabel,
      });
      break;
    }

    case 'recipe': {
      const r = d as unknown as RecipeEvent;
      const recipes = [..._state.recipes];
      const idx = recipes.findIndex(x => x.name === r.name);
      if (idx >= 0) recipes[idx] = r; else recipes.push(r);
      emit({ recipes });
      break;
    }

    case 'done': {
      const done = d as unknown as DoneEvent;
      emit({ isRunning: false, status: 'completed', pct: 100, planId: done.plan_id, summary: done.summary, stepLabel: 'Fertig!', needsFollowUpPlan: !!done.needs_follow_up_plan, followUpWeekStart: done.follow_up_week_start || null });
      cleanup();
      break;
    }

    case 'error':
      emit({ isRunning: false, status: 'failed', error: (d.message as string) || 'Unbekannter Fehler', errorStep: (d.step as number) || null });
      cleanup();
      break;

    case 'aborted':
      emit({ isRunning: false, status: 'aborted', stepLabel: 'Abgebrochen' });
      cleanup();
      break;
  }
}

// ── Polling Fallback ──

function startPolling(jobId: string) {
  if (_pollingInterval) return;
  _pollingInterval = setInterval(async () => {
    try {
      const job = await get<{
        status: string; current_step: number; step_label: string;
        plan_id: number | null; result_summary: PlanSummary | null;
        error_message: string | null; error_step: number | null;
      }>(`/ai/plan-jobs/${jobId}`);

      emit({
        status: job.status, currentStep: job.current_step, stepLabel: job.step_label || '',
        planId: job.plan_id, summary: job.result_summary, error: job.error_message,
        errorStep: job.error_step, pct: Math.round((job.current_step / 5) * 100),
        isRunning: !['completed', 'failed', 'aborted'].includes(job.status),
      });

      if (['completed', 'failed', 'aborted'].includes(job.status)) {
        cleanup();
      }
    } catch { /* ignore */ }
  }, 2000);
}

function stopPolling() {
  if (_pollingInterval) { clearInterval(_pollingInterval); _pollingInterval = null; }
}

function cleanup() {
  _sseController?.abort();
  _sseController = null;
  stopPolling();
}

// ── Init: check for active job (runs once globally) ──

function ensureInit() {
  if (_initDone) return;
  _initDone = true;

  get<{ active: boolean; job_id?: string; status?: string; current_step?: number; step_label?: string }>('/ai/plan-jobs/active')
    .then(res => {
      if (res.active && res.job_id) {
        emit({
          jobId: res.job_id,
          isRunning: true,
          status: res.status || 'running',
          currentStep: res.current_step || 0,
          stepLabel: res.step_label || '',
        });
        connectSSE(res.job_id);
      }
    })
    .catch(() => { /* not logged in yet */ });
}

// ── Actions ──

async function startJob(params: PlanJobParams): Promise<string | null> {
  try {
    const res = await post<{ job_id: string }>('/ai/plan-jobs', params);

    emitFull({
      ...INITIAL,
      jobId: res.job_id,
      isRunning: true,
      status: 'queued',
      stepLabel: 'Starte...',
    });

    connectSSE(res.job_id);
    return res.job_id;
  } catch (err: unknown) {
    const e = err as { status?: number; message?: string };
    if (e.status === 409) {
      try {
        const parsed = JSON.parse(e.message || '{}');
        emit({ jobId: parsed.job_id, isRunning: true, error: null });
        connectSSE(parsed.job_id);
        return parsed.job_id;
      } catch { /* fallthrough */ }
    }
    emit({ error: e.message || 'Konnte Job nicht starten', isRunning: false });
    return null;
  }
}

async function abortJob(): Promise<void> {
  if (!_state.jobId) return;
  try { await del(`/ai/plan-jobs/${_state.jobId}`); } catch { /* ignore */ }
}

function reset(): void {
  cleanup();
  emitFull({ ...INITIAL });
}

// ── Hook ──

export function usePlanJob(): UsePlanJobReturn {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // Trigger init on first hook mount
  ensureInit();

  const startJobCb = useCallback((params: PlanJobParams) => startJob(params), []);
  const abortJobCb = useCallback(() => abortJob(), []);
  const resetCb = useCallback(() => reset(), []);

  return { ...state, startJob: startJobCb, abortJob: abortJobCb, reset: resetCb };
}
